import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { 
  Package, AlertCircle, Loader2, ShieldAlert,
  Mail, History, ArrowUpRight, Trash2, Calendar
} from "lucide-react";

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const token = user?.accessToken || user?.token;

  // --- 1. Real-Time Date & Time Helper ---
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    
    // Relative time (e.g., 5m ago)
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    let relative = "";
    if (diffInSeconds < 60) relative = "Just Now";
    else if (diffInSeconds < 3600) relative = `${Math.floor(diffInSeconds / 60)}m ago`;
    else if (diffInSeconds < 86400) relative = `${Math.floor(diffInSeconds / 3600)}h ago`;
    else relative = date.toLocaleDateString();

    // Exact Time (e.g., 12:45 PM)
    const exactTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const exactDate = date.toLocaleDateString([], { day: '2-digit', month: 'short' });

    return { relative, exact: `${exactTime} - ${exactDate}` };
  };

  const fetchRequests = async () => {
    if (!token) return;
    try {
      const res = await axios.get("https://wethrift-backend.onrender.com/api/requests", {
        headers: { token: `Bearer ${token}` } 
      });
      setRequests(res.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch requests protocol.");
    } finally {
      setLoading(false);
    }
  };

  // --- 2. Remove / Delete Logic ---
  const handleDelete = async (id) => {
    if (!window.confirm("WARNING: This will permanently delete this record from the archive. Proceed?")) return;
    
    try {
      await axios.delete(`https://wethrift-backend.onrender.com/api/requests/${id}`, {
        headers: { token: `Bearer ${token}` }
      });
      alert("Record purged successfully.");
      setRequests(requests.filter(req => req._id !== id));
    } catch (err) {
      alert(err.response?.data?.message || "Error deleting record");
    }
  };

  const handleStatusUpdate = async (id, decision) => {
    const confirmMsg = decision === 'approved' 
      ? "Approve this return? 30% will be deducted and balance sent to user wallet."
      : "Are you sure you want to REJECT this request?";
      
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await axios.put(`https://wethrift-backend.onrender.com/api/requests/process/${id}`, 
        { status: decision }, 
        { headers: { token: `Bearer ${token}` } }
      );
      alert(res.data.message || `Protocol ${decision} executed.`);
      fetchRequests(); 
    } catch (err) {
      alert(err.response?.data?.message || "Error updating status");
    }
  };

  useEffect(() => {
    if (token) {
      fetchRequests();
      const interval = setInterval(fetchRequests, 60000);
      return () => clearInterval(interval);
    }
  }, [token]);

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-black text-white">
      <Loader2 className="animate-spin mr-2 text-blue-500" /> 
      <span className="font-black uppercase tracking-widest text-[10px] italic">Syncing_Archive_Queue...</span>
    </div>
  );

  return (
    <div className="p-8 bg-black min-h-screen text-white font-sans selection:bg-white selection:text-black">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-16 border-b border-white/5 pb-10 gap-6">
        <div>
          <h1 className="text-5xl font-black italic uppercase tracking-tighter leading-none">
            Audit_Queue<span className="text-zinc-800">.</span>
          </h1>
          <p className="text-zinc-500 text-[10px] uppercase font-bold tracking-[0.4em] mt-3 flex items-center gap-2">
            <ShieldAlert size={12} className="text-blue-500" />
            Live terminal active | {requests.length} Pending
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {requests.map((req) => {
          const timeInfo = formatDateTime(req.createdAt);
          return (
            <div key={req._id} className="relative bg-[#080808] border border-white/5 p-8 rounded-[2.5rem] group overflow-hidden">
              
              {/* REMOVE BUTTON (TOP RIGHT) */}
              <button 
                onClick={() => handleDelete(req._id)}
                className="absolute top-6 right-6 p-2 text-zinc-700 hover:text-red-500 transition-colors z-20"
                title="Delete Record"
              >
                <Trash2 size={18} />
              </button>

              <div className="flex flex-col h-full justify-between relative z-10">
                <div>
                  <div className="mb-6">
                    <span className="bg-white/5 text-blue-400 text-[9px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest border border-white/5">
                      ID: {req.order_id}
                    </span>
                    <div className="mt-3 flex flex-col gap-1">
                       <span className="text-[9px] font-black text-zinc-500 uppercase flex items-center gap-2">
                         <History size={12} className="text-blue-500"/> {timeInfo.relative}
                       </span>
                       <span className="text-[8px] font-bold text-zinc-700 uppercase flex items-center gap-2">
                         <Calendar size={10}/> {timeInfo.exact}
                       </span>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-black uppercase tracking-tighter mb-1 italic">
                    {req.user_name || "User_Undefined"}
                  </h3>
                  <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-6 border-b border-white/5 pb-4">
                    {req.user_email}
                  </p>
                  
                  <div className="bg-white/[0.02] border border-white/5 p-5 rounded-3xl mb-8 group-hover:bg-white/[0.04] transition-colors">
                    <p className="text-[11px] font-black text-zinc-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <ArrowUpRight size={10} /> Message_Log
                    </p>
                    <p className="text-zinc-400 text-xs leading-relaxed italic font-medium">
                      "{req.message || "No contextual data provided."}"
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {req.status === 'pending' ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleStatusUpdate(req._id, 'approved')}
                          className="flex-1 bg-white text-black py-4 rounded-xl font-black text-[10px] uppercase hover:bg-green-500 hover:text-white transition-all transform active:scale-95"
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => handleStatusUpdate(req._id, 'rejected')}
                          className="flex-1 bg-zinc-900 text-zinc-400 py-4 rounded-xl font-black text-[10px] uppercase hover:bg-red-500 hover:text-white border border-white/5 transition-all transform active:scale-95"
                        >
                          Reject
                        </button>
                      </div>
                      <a 
                        href={`mailto:${req.user_email}?subject=Order ID: ${req.order_id}`}
                        className="w-full bg-zinc-950 text-zinc-500 py-3 rounded-xl font-black text-[9px] uppercase tracking-widest text-center border border-white/5 hover:text-white transition-all flex items-center justify-center gap-2"
                      >
                        <Mail size={12} /> Contact_User
                      </a>
                    </div>
                  ) : (
                    <div className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] text-center border ${
                      req.status === 'approved' 
                        ? 'border-green-500/20 text-green-500 bg-green-500/5' 
                        : 'border-red-500/20 text-red-500 bg-red-500/5'
                    }`}>
                      Protocol_{req.status}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminRequests;